/** Sources tab (A7Y-4883): first-source counts grouped by family, with family subtotals. */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type {
  KeywordDatabaseSourceRow,
  KeywordDatabaseSources,
  KeywordDatabaseStore,
} from '@a7y/types';
import { formatInt } from '../format';
import { FAMILY_ORDER, OTHER_FAMILY, sourceFamily, type SourceFamily } from '../source-family';

const STORE_LABEL: Record<KeywordDatabaseStore, string> = {
  appstore: 'App Store',
  playstore: 'Play Store',
};

function groupByFamily(rows: readonly KeywordDatabaseSourceRow[]) {
  const groups = new Map<SourceFamily, KeywordDatabaseSourceRow[]>();
  for (const row of rows) {
    const family = sourceFamily(row.source);
    groups.set(family, [...(groups.get(family) ?? []), row]);
  }
  return FAMILY_ORDER.filter((f) => groups.has(f)).map((family) => {
    const members = [...(groups.get(family) ?? [])].sort((a, b) => b.allTime - a.allTime);
    return {
      family,
      members,
      allTime: members.reduce((n, r) => n + r.allTime, 0),
      last48h: members.reduce((n, r) => n + r.last48h, 0),
    };
  });
}

export function SourcesTab({ sources }: { sources: KeywordDatabaseSources }) {
  const groups = groupByFamily(sources.rows);
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        Each keyword is counted once, under the source of its first sighting. Last 48 h counts
        keywords whose first sighting fell in the last 48 hours.
      </p>
      <Table dense data-testid="sources-table">
        <TableHead>
          <TableRow>
            <TableHeader>Source</TableHeader>
            <TableHeader>Store</TableHeader>
            <TableHeader>All-time</TableHeader>
            <TableHeader>Last 48 h</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.flatMap((g) => [
            <TableRow
              key={`family-${g.family}`}
              data-testid={`family-subtotal-${g.family}`}
              className="bg-zinc-50 font-semibold dark:bg-zinc-800/60"
            >
              <TableCell>{g.family}</TableCell>
              <TableCell />
              <TableCell className="tabular-nums">{formatInt(g.allTime)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(g.last48h)}</TableCell>
            </TableRow>,
            ...g.members.map((r) => (
              <TableRow key={`${r.source}-${r.store}`} data-testid={`source-row-${r.source}`}>
                <TableCell className="pl-6 font-mono text-xs">
                  {r.source}
                  {g.family === OTHER_FAMILY && (
                    <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 font-sans text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                      not mapped to a family yet
                    </span>
                  )}
                </TableCell>
                <TableCell>{STORE_LABEL[r.store]}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.allTime)}</TableCell>
                <TableCell className="tabular-nums">{formatInt(r.last48h)}</TableCell>
              </TableRow>
            )),
          ])}
        </TableBody>
      </Table>
    </div>
  );
}
